// src/pages/admin/AdminIncidentsPage.jsx
import { useCallback, useEffect, useState } from "react";
import {
  Box, Paper, Stack, Typography, Chip, IconButton, Button, TextField,
  Select, MenuItem, InputLabel, FormControl, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  CircularProgress, LinearProgress, InputAdornment, Divider,
} from "@mui/material";
import RefreshIcon        from "@mui/icons-material/Refresh";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import SearchIcon         from "@mui/icons-material/Search";
import VisibilityIcon     from "@mui/icons-material/Visibility";
import WarningAmberIcon   from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon   from "@mui/icons-material/ErrorOutline";
import BugReportIcon      from "@mui/icons-material/BugReport";
import CreditCardIcon     from "@mui/icons-material/CreditCard";
import MoneyOffIcon       from "@mui/icons-material/MoneyOff";
import CloseIcon          from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { pageWrap } from "../../utils/pageStyles";

const SEVERITY_META = {
  critical: { label: "Critique",  color: "#dc2626", bg: "#fef2f2" },
  high:     { label: "Haute",     color: "#ea580c", bg: "#fff7ed" },
  medium:   { label: "Moyenne",   color: "#d97706", bg: "#fffbeb" },
  low:      { label: "Basse",     color: "#65a30d", bg: "#f7fee7" },
};

const TYPE_LABELS = {
  lab_start_failed:  "Démarrage échoué",
  lab_stop_failed:   "Arrêt échoué",
  lab_crash:         "Lab planté",
  node_start_failed: "Nœud non démarré",
  gns3_unreachable:  "GNS3 injoignable",
  debit_failed:      "Débit échoué",
  refund_failed:     "Remboursement échoué",
  double_debit:      "Double débit",
  scheduler_misfire: "Job manqué",
  api_error_500:     "Erreur 500",
  other:             "Autre",
};

function SeverityChip({ severity }) {
  const m = SEVERITY_META[severity] || SEVERITY_META.medium;
  return (
    <Chip size="small" label={m.label}
      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: m.bg, color: m.color }} />
  );
}

function KpiCard({ value, label, color, icon }) {
  return (
    <Box sx={{ flex: 1, bgcolor: "#f8fafc", borderRadius: 2, p: 2,
      border: "1px solid #e2e8f0", textAlign: "center" }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
        {icon}
        <Typography variant="h5" fontWeight={700} sx={{ color }}>{value}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function DetailDialog({ incident, open, onClose, onResolve, resolving }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (open) setNote(""); }, [open]);
  if (!incident) return null;
  const m = SEVERITY_META[incident.severity] || SEVERITY_META.medium;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <BugReportIcon sx={{ color: m.color, fontSize: 20 }} />
            <Typography fontWeight={700}>Incident #{incident.id}</Typography>
            <SeverityChip severity={incident.severity} />
            {incident.resolved === 1 && (
              <Chip size="small" label="Résolu" sx={{ height: 20, fontSize: 10,
                fontWeight: 700, bgcolor: "#f0fdf4", color: "#16a34a" }} />
            )}
          </Stack>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 2 }}>
            <Stack spacing={1}>
              {[
                ["Type",        TYPE_LABELS[incident.incident_type] || incident.incident_type],
                ["Message",     incident.message],
                ["Date",        incident.created_at ? new Date(incident.created_at).toLocaleString("fr") : "—"],
                ["Réservation", incident.reservation_id ? `#${incident.reservation_id}` : "—"],
                ["Utilisateur", incident.user_id ? `#${incident.user_id}` : "—"],
                ["Lab",         incident.lab_id ? `#${incident.lab_id}` : "—"],
                ["Code erreur", incident.error_code || "—"],
              ].map(([k, v]) => (
                <Stack key={k} direction="row" spacing={2}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>{k}</Typography>
                  <Typography variant="caption" fontWeight={600}>{v}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {incident.details && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: .5, fontSize: 10 }}>
                Détails techniques
              </Typography>
              <Box sx={{ bgcolor: "#0f172a", borderRadius: 2, p: 2, maxHeight: 260, overflowY: "auto",
                fontFamily: "monospace", fontSize: 11, color: "#e2e8f0", whiteSpace: "pre-wrap",
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "#334155", borderRadius: 2 } }}>
                {typeof incident.details === "string"
                  ? incident.details
                  : JSON.stringify(incident.details, null, 2)}
              </Box>
            </Box>
          )}

          {incident.resolved === 1 && (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Résolu le {incident.resolved_at ? new Date(incident.resolved_at).toLocaleString("fr") : "—"}
              {incident.resolution_note && ` — ${incident.resolution_note}`}
            </Alert>
          )}

          {incident.resolved === 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Note de résolution (optionnel)
              </Typography>
              <TextField size="small" fullWidth multiline rows={2}
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Décris l'action corrective effectuée..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Fermer</Button>
        {incident.resolved === 0 && (
          <Button variant="contained" onClick={() => onResolve(incident.id, note)}
            disabled={resolving}
            startIcon={resolving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
            sx={{ borderRadius: 2, bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}>
            Marquer comme résolu
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}


// ── Dialog remboursement manuel ───────────────────────────────────────────────
function RefundDialog({ open, onClose, incident, onSuccess }) {
  const [minutes,  setMinutes]  = useState("");
  const [reason,   setReason]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [detail,   setDetail]   = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!open) { setMinutes(""); setReason(""); setError(""); setDetail(null); return; }
    if (!incident?.reservation_id) return;
    setLoadingDetail(true);
    api.get(`/admin/reservations/${incident.reservation_id}/detail`)
      .then(({ data }) => setDetail(data))
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }, [open, incident]);

  const fmtDuration = (min) => {
    if (!min) return "—";
    const h = Math.floor(min / 60), m = min % 60;
    return h > 0 ? `${h}h ${m > 0 ? m + "min" : ""}`.trim() : `${m} min`;
  };

  const handleSubmit = async () => {
    if (!minutes || Number(minutes) <= 0) return setError("Nombre de minutes invalide");
    if (!reason.trim()) return setError("Le motif est obligatoire");
    if (!incident?.reservation_id) return setError("Aucune réservation liée à cet incident");
    setLoading(true); setError("");
    try {
      const { data } = await api.post(
        `/admin/reservations/${incident.reservation_id}/refund`,
        { minutes: Number(minutes), reason: reason.trim() }
      );
      onSuccess(
        `✅ ${data.minutes_refunded} min remboursées — ` +
        `Wallet #${data.wallet_id} : ${data.wallet_before} → ${data.wallet_after} min`
      );
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors du remboursement");
    } finally { setLoading(false); }
  };

  if (!incident) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <CreditCardIcon sx={{ color: "#6366f1", fontSize: 20 }} />
            <Typography fontWeight={700}>Remboursement manuel</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2}>
          {/* Contexte incident + détails réservation */}
          <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 2, border: "1px solid #e2e8f0" }}>
            {loadingDetail
              ? <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={12} />
                  <Typography variant="caption" color="text.secondary">Chargement…</Typography>
                </Stack>
              : (
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Incident</Typography>
                  <Typography variant="caption" fontWeight={700}>#{incident.id} — {incident.incident_type}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Réservation</Typography>
                  <Typography variant="caption" fontWeight={700}>
                    #{incident.reservation_id}
                    {detail?.lab_name && <span style={{ fontWeight: 400, color: "#64748b" }}> — {detail.lab_name}</span>}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Statut</Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: "uppercase", fontSize: 10,
                    color: detail?.status === "failed" ? "#ef4444" : detail?.status === "completed" ? "#22c55e" : "#f59e0b" }}>
                    {detail?.status || "—"}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Durée planifiée</Typography>
                  <Typography variant="caption" fontWeight={700}>{fmtDuration(detail?.duration_minutes)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Utilisateur</Typography>
                  <Typography variant="caption" fontWeight={700}>
                    {detail?.user?.username || `#${incident.user_id}`}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="caption" color="text.secondary">{detail?.user?.email || "—"}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Crédits débités</Typography>
                  <Typography variant="caption" fontWeight={700}>{detail?.credits?.debited ?? "—"} min</Typography>
                </Stack>
                {(detail?.credits?.already_refunded || 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Déjà remboursé</Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#22c55e" }}>
                      {detail.credits.already_refunded} min
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Max remboursable</Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: "#6366f1" }}>
                    {detail?.credits?.max_refundable ?? "—"} min
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>

          <TextField
            label="Minutes à rembourser"
            type="number"
            size="small"
            fullWidth
            value={minutes}
            onChange={e => setMinutes(e.target.value)}
            inputProps={{ min: 1, max: detail?.credits?.max_refundable || undefined }}
            placeholder={detail?.credits?.max_refundable != null
              ? `Max : ${detail.credits.max_refundable} min`
              : "Ex: 60"}
            helperText={detail?.credits?.max_refundable != null
              ? `Maximum remboursable : ${detail.credits.max_refundable} min`
              : ""}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          <TextField
            label="Motif du remboursement"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Ex: Lab GNS3 injoignable — timeout connexion, lab non utilisé"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          {minutes && Number(minutes) > 0 && (
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: 12 }}>
              {Number(minutes)} minute{Number(minutes) > 1 ? "s" : ""} seront créditées sur le wallet
              de la réservation #{incident.reservation_id}.
            </Alert>
          )}

          {!incident.reservation_id && (
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 12 }}>
              Cet incident n'est pas lié à une réservation — le remboursement n'est pas possible.
            </Alert>
          )}

          {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }} disabled={loading}>Annuler</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !incident.reservation_id || !minutes || !reason.trim()}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CreditCardIcon />}
          sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, flex: 1 }}
        >
          {loading ? "Remboursement..." : "Rembourser"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminIncidentsPage() {
  const { t } = useTranslation();

  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [q,         setQ]         = useState("");
  const [severity,  setSeverity]  = useState("");
  const [typeF,     setTypeF]     = useState("");
  const [resolved,  setResolved]  = useState("0");
  const [page,      setPage]      = useState(0);
  const PAGE = 50;

  const [selected,  setSelected]  = useState(null);
  const [resolving, setResolving] = useState(false);
  const [snack,     setSnack]     = useState(null);
  const [refundInc, setRefundInc] = useState(null);

  const load = useCallback(async (pg = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset: pg * PAGE };
      if (q.trim())    params.q             = q.trim();
      if (severity)    params.severity      = severity;
      if (typeF)       params.incident_type = typeF;
      if (resolved !== "") params.resolved  = resolved;
      const { data: d } = await api.get("/admin/incidents", { params });
      setData(d);
    } catch (e) {
      setSnack({ text: "Erreur chargement incidents", sev: "error" });
    } finally { setLoading(false); }
  }, [q, severity, typeF, resolved]);

  useEffect(() => { setPage(0); load(0); }, [load]);

  const handleResolve = async (id, note) => {
    setResolving(true);
    try {
      await api.post(`/admin/incidents/${id}/resolve`, { note: note || null });
      setSnack({ text: "Incident marqué comme résolu", sev: "success" });
      setSelected(null);
      load(page);
    } catch { setSnack({ text: "Erreur lors de la résolution", sev: "error" }); }
    finally { setResolving(false); }
  };

  const items       = data?.items || [];
  const total       = data?.total || 0;
  const openCount   = data?.open_count || 0;
  const critCount   = data?.critical_count || 0;
  const totalPages  = Math.max(1, Math.ceil(total / PAGE));

  return (
    <Box sx={pageWrap}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
        alignItems={{ sm: "flex-start" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#fef2f2",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BugReportIcon sx={{ fontSize: 20, color: "#dc2626" }} />
            </Box>
            <Typography variant="h5" fontWeight={700}>Journal des incidents</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Incidents labs, crédits et système persistés automatiquement.
          </Typography>
        </Box>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={() => load(page)} disabled={loading}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* KPIs */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <KpiCard value={total}     label="Total incidents" color="#6366f1"
          icon={<BugReportIcon sx={{ fontSize: 18, color: "#6366f1" }} />} />
        <KpiCard value={openCount}  label="Incidents ouverts" color="#ea580c"
          icon={<WarningAmberIcon sx={{ fontSize: 18, color: "#ea580c" }} />} />
        <KpiCard value={critCount}  label="Critique / Haute priorité" color="#dc2626"
          icon={<ErrorOutlineIcon sx={{ fontSize: 18, color: "#dc2626" }} />} />
        <KpiCard value={total - openCount} label="Résolus" color="#22c55e"
          icon={<CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e" }} />} />
      </Stack>

      {/* Filtres */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
        borderRadius: 3, p: 2.5, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
          <TextField size="small" placeholder="Rechercher..." value={q}
            onChange={e => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
            </InputAdornment>, sx: { borderRadius: 2 } }}
            sx={{ minWidth: 220 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Sévérité</InputLabel>
            <Select value={severity} label="Sévérité"
              onChange={e => setSeverity(e.target.value)} sx={{ borderRadius: 2 }}>
              <MenuItem value="">Toutes</MenuItem>
              {Object.entries(SEVERITY_META).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeF} label="Type"
              onChange={e => setTypeF(e.target.value)} sx={{ borderRadius: 2 }}>
              <MenuItem value="">Tous</MenuItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Statut</InputLabel>
            <Select value={resolved} label="Statut"
              onChange={e => setResolved(e.target.value)} sx={{ borderRadius: 2 }}>
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="0">Ouverts</MenuItem>
              <MenuItem value="1">Résolus</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Tableau */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
        borderRadius: 3, overflow: "hidden" }}>
        {loading && <LinearProgress sx={{ height: 2 }} />}
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor: "#f8fafc" }}>
              {["#", "Date", "Type", "Sévérité", "Message", "Réservation", "Statut", ""].map((h, i) => (
                <Box component="th" key={i} sx={{
                  p: 1.5, textAlign: "left", fontSize: 11, fontWeight: 700,
                  color: "text.secondary", borderBottom: "1px solid #e2e8f0",
                  textTransform: "uppercase", letterSpacing: .4,
                  ...(i === 7 ? { textAlign: "right" } : {})
                }}>{h}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {items.length === 0 && !loading ? (
              <Box component="tr">
                <Box component="td" colSpan={8} sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                  <BugReportIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1, display: "block", mx: "auto" }} />
                  Aucun incident trouvé
                </Box>
              </Box>
            ) : items.map(inc => (
              <Box component="tr" key={inc.id} sx={{
                "&:hover": { bgcolor: "#f8fafc" },
                "& td": { p: 1.5, borderBottom: "1px solid #f1f5f9", fontSize: 13 },
                "&:last-child td": { borderBottom: "none" },
                opacity: inc.resolved === 1 ? 0.6 : 1,
              }}>
                <Box component="td" sx={{ color: "text.disabled", fontFamily: "monospace", fontSize: 11 }}>
                  #{inc.id}
                </Box>
                <Box component="td" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontSize: 12 }}>
                  {inc.created_at ? new Date(inc.created_at).toLocaleString("fr") : "—"}
                </Box>
                <Box component="td">
                  <Typography variant="caption" fontWeight={600}>
                    {TYPE_LABELS[inc.incident_type] || inc.incident_type}
                  </Typography>
                </Box>
                <Box component="td"><SeverityChip severity={inc.severity} /></Box>
                <Box component="td" sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" noWrap sx={{ fontSize: 12 }}>{inc.message}</Typography>
                </Box>
                <Box component="td" sx={{ color: "text.secondary", fontSize: 12 }}>
                  {inc.reservation_id ? `#${inc.reservation_id}` : "—"}
                </Box>
                <Box component="td">
                  {inc.resolved === 1
                    ? <Chip size="small" label="Résolu" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#f0fdf4", color: "#16a34a" }} />
                    : <Chip size="small" label="Ouvert" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626" }} />
                  }
                </Box>
                <Box component="td" sx={{ textAlign: "right" }}>
                  <Tooltip title="Voir détails">
                    <IconButton size="small" onClick={() => setSelected(inc)}
                      sx={{ "&:hover": { bgcolor: "#eef2ff" } }}>
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {inc.reservation_id && inc.resolved === 0 && (
                    <Tooltip title="Rembourser">
                      <IconButton size="small" onClick={() => setRefundInc(inc)}
                        sx={{ "&:hover": { bgcolor: "#eef2ff" }, color: "#6366f1" }}>
                        <CreditCardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Pagination */}
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            {total} incident{total !== 1 ? "s" : ""} — Page {page + 1} / {totalPages}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" disabled={page === 0 || loading}
              onClick={() => { const p = page - 1; setPage(p); load(p); }}
              sx={{ borderRadius: 2, fontSize: 12 }}>← Précédent</Button>
            <Button size="small" variant="outlined" disabled={(page + 1) * PAGE >= total || loading}
              onClick={() => { const p = page + 1; setPage(p); load(p); }}
              sx={{ borderRadius: 2, fontSize: 12 }}>Suivant →</Button>
          </Stack>
        </Stack>
      </Paper>

      <DetailDialog
        incident={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        onResolve={handleResolve}
        resolving={resolving}
      />

      <RefundDialog
        open={Boolean(refundInc)}
        incident={refundInc}
        onClose={() => setRefundInc(null)}
        onSuccess={msg => { setSnack({ text: msg, sev: "success" }); load(page); }}
      />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack?.sev || "info"} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
